import {
  ApolloClient,
  InMemoryCache,
  from,
  split,
  createHttpLink,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { WebSocketLink } from 'apollo-link-ws';
import { getMainDefinition } from 'apollo-utilities';
import { getItem, LocalStorageKey, removeItem } from 'services/persistentData';
import { TokenRefreshLink } from 'apollo-link-token-refresh';
import errorHandler from './errors';
import moment, { Moment } from 'moment';
import { jwtDecode } from 'jwt-decode';
import authService from 'services/cognito/lib';
const httpLink = createHttpLink({
  uri: window.config.API_GRAPHQL_ENDPOINT,
});

const TOKEN_EXPIRATION_OFFSET = 5;

const getExpirationTokenInfoFromExp = (
  exp: number
): {
  emissionDate: string;
  expirationDate: string;
  expireIn: number;
} => {
  const momentNowDate: Moment = moment();
  const momentExpirationDate: Moment = moment(exp * 1000);
  const expireIn: number = momentExpirationDate.diff(momentNowDate, 's');
  const emissionDate: string = momentNowDate.toISOString();
  const expirationDate: string = momentExpirationDate
    .subtract(TOKEN_EXPIRATION_OFFSET, 's')
    .toISOString();

  return { emissionDate, expirationDate, expireIn };
};

const updateAccessToken = (accessToken: string) => {
  const decodedAccessToken = jwtDecode(accessToken);
  const { exp } = decodedAccessToken as { exp: number };
  const { expirationDate } = getExpirationTokenInfoFromExp(exp);
  authService.setSession({
    accessToken,
    expiration: expirationDate,
  });
};

const promiseResponse = (response: any): Promise<boolean> =>
  new Promise((resolve) => {
    resolve(!!response);
  });

export const refreshLink = new TokenRefreshLink({
  accessTokenField: 'accessToken',
  isTokenValidOrUndefined: (): Promise<boolean> => {
    const expirationDate = getItem(LocalStorageKey.TOKEN_EXPIRATION_DATE);
    if (!expirationDate) {
      return promiseResponse(!expirationDate);
    }
    const tokenIsValid = moment(expirationDate).isAfter(moment().toISOString());
    return promiseResponse(tokenIsValid);
  },
  fetchAccessToken: async (): Promise<Response> => {
    try {
      const results = await authService.refreshSession();
      if (!results) {
        throw new Error('No results from refreshSession');
      }
      const response = new Response(
        JSON.stringify({ accessToken: results.token }),
        {
          status: 200,
          statusText: 'OK',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  },
  handleError: (_error) => {
    authService.logout({
      cleanSessionInfoCallback: () => {
        removeItem(LocalStorageKey.ACCESS_TOKEN);
      },
    });
  },
  handleFetch: updateAccessToken,
  handleResponse: () => async (result: any) => {
    return await result.json();
  },
});

const wsLink = new WebSocketLink({
  uri: window.config.API_GRAPHQL_ENDPOINT.replace(/^http(s)?/, 'ws$1'),
  options: {
    reconnect: true,
    connectionParams: () => {
      const token = getItem(LocalStorageKey.ACCESS_TOKEN);
      return { authorization: token ? `Bearer ${token}` : '' };
    },
  },
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = getItem(LocalStorageKey.ACCESS_TOKEN);

  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const errorLink = onError(errorHandler.onError);

export const client = new ApolloClient({
  link: from([
    // @ts-ignore
    refreshLink,
    errorLink,
    authLink,

    // Use Websockets for subscriptions, and HTTP otherwise.
    split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      // @ts-ignore
      wsLink,
      httpLink
    ),
  ]),
  cache: new InMemoryCache(),
});
